import type { FC, ReactNode } from 'react'
import { Briefcase, CheckCircle2, PenLine, Clock3, XCircle, CircleCheckBigIcon } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { Loader } from '@/components/ui'
import { useApplicationStats } from '@/lib/api/hooks/application'


interface StatsCardProps {
	title: string
	value: string | number
	icon: ReactNode
	iconContainerClassName: string
	iconClassName: string
	percentageChange?: number
	id: string
}

interface StatConfig {
	key: string
	title: string
	icon: LucideIcon
	iconContainerClassName: string
	iconClassName: string
}

const StatCard = ({
	title,
	value,
	icon,
	iconContainerClassName,
	iconClassName,
	percentageChange,
	id
}: StatsCardProps) => {

	return (
		<article className="rounded-xl border border-gray-200 bg-white px-5 py-4 shadow-sm">
			<div className="grid grid-cols-[auto_1fr] gap-4">
				<div className={`flex h-12 w-12 items-center justify-center rounded-md ${iconContainerClassName}`}>
					<span className={iconClassName}>{icon}</span>
				</div>
				<div>
					<p className="text-base font-medium text-gray-600">{title}</p>
					<p className="text-[2rem] leading-[1.1] font-semibold text-gray-900">{value}</p>
							{percentageChange !== undefined && (
				<p className="text-sm text-gray-500 mt-1">
					{percentageChange > 0 ? '+' : ''}
					{percentageChange}% {id === 'totalApplications' ? 'vs last 30 days' : 'of total '}
				</p>
			)}
				</div>
			</div>
	
		</article>
	)
}



const STATS_CONFIG: StatConfig[] = [
	{
		key: 'totalApplications',
		title: 'Total Applications',
		icon: Briefcase,
		iconContainerClassName: 'bg-[#E8F6EE80]',
		iconClassName: 'text-[#0F8A47]',
	},
	{
		key: 'newApplications',
		title: 'New',
		icon: CheckCircle2,
		iconContainerClassName: 'bg-[#E8F6EE80]',
		iconClassName: 'text-[#0F8A47]',
	},
	{
		key: 'reviewApplications',
		title: 'In Review',
		icon: PenLine,
		iconContainerClassName: 'bg-[#FDF3E680]',
		iconClassName: 'text-[#B86A00]',
	},
	{
		key: 'interviewApplications',
		title: 'Interview',
		icon: Clock3,
		iconContainerClassName: 'bg-[#FDF3E680]',
		iconClassName: 'text-[#C2650A]',
	},
	{
		key: 'rejectedApplications',
		title: 'Rejected',
		icon: XCircle,
		iconContainerClassName: 'bg-[#FFE8E8]',
		iconClassName: 'text-[tomato]',
	},
	{
		key: 'hiredApplications',
		title: 'Hired',
		icon: CircleCheckBigIcon,
	iconContainerClassName: 'bg-[#E8F6EE80]',
		iconClassName: 'text-[#0F8A47]',
	}
]

const ApplicationsStats: FC = () => {
	const { data: stats, isLoading, isError, refetch } = useApplicationStats()

	if (isError) {
		return (
			<div className="p-4 flex flex-col items-center justify-center h-full w-full gap-4 sm:col-span-2 xl:col-span-5">
				<p>Error loading statistics.</p>
				<button onClick={() => refetch()} className="px-4 py-2 bg-forestGreen-100 text-white rounded">
					Retry
				</button>
			</div>
		)
	}

	if (isLoading) {
		return (
			<div className="flex items-center justify-center">
				<Loader size="md" variant="wine" message="Loading statistics..." />
			</div>
		)
	}

	const values = {
		totalApplications: stats?.totalApplications ?? 0,
		newApplications: stats?.newApplications ?? 0,
		reviewApplications: stats?.reviewApplications ?? 0,
		interviewApplications: stats?.interviewApplications ?? 0,
		rejectedApplications: stats?.rejectedApplications ?? 0,
		hiredApplications: stats?.hiredApplications ?? 0,
	}

	const percentageChanges = {
		totalApplications: stats?.totalApplicationsPercentageChange ?? 0,
		newApplications: stats?.newApplicationsPercentageOfTotal ?? 0,
		reviewApplications: stats?.reviewApplicationsPercentageOfTotal ?? 0,
		interviewApplications: stats?.interviewApplicationsPercentageOfTotal ?? 0,
		rejectedApplications: stats?.rejectedApplicationsPercentageOfTotal ?? 0,
		hiredApplications: stats?.hiredApplicationsPercentageOfTotal ?? 0,
	}

	return (
		<section className="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6 2xl:w-full">
			{STATS_CONFIG.map((config) => {
				const Icon = config.icon
				return (
					<StatCard
						key={config.key}
						id={config.key}
						title={config.title}
						value={values[config.key as keyof typeof values]}
						percentageChange={percentageChanges[config.key as keyof typeof percentageChanges]}
						icon={<Icon className="w-6 h-6" />}
						iconContainerClassName={config.iconContainerClassName}
						iconClassName={config.iconClassName}
					/>
				)
			})}
		</section>
	)
}

export default ApplicationsStats
